import React, { useState, useEffect, useCallback } from 'react';
import styles from './CaseLogSettings.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Icon from '../../../components/icon/Icon';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';

const CaseLogSettings = () => {
  const { showToast } = useToasts();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get('/case-log/report-rows');
      setRows(response.data || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleEditClick = (row) => {
    setEditingKey(row.key);
    setEditValue(row.totalNumberOfRequirement ?? '');
  };

  const handleSave = async (key) => {
    setIsSaving(true);
    try {
      await axiosClient.patch('/case-log/report-rows/requirements', {
        items: [{ key, totalNumberOfRequirement: editValue === '' ? null : Number(editValue) }]
      });
      setRows(prev => prev.map(r => r.key === key ? { ...r, totalNumberOfRequirement: editValue === '' ? null : Number(editValue) } : r));
      setEditingKey(null);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e, key) => {
    if (e.key === 'Enter') handleSave(key);
    if (e.key === 'Escape') setEditingKey(null);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Case Log Settings" />
      </div>
      <div className={styles.container}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Case Log Fields</th>
                <th>Total Required</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className={styles.labelCell}>{row.label}</td>
                  <td className={styles.valueCell}>
                    {editingKey === row.key ? (
                      <input
                        className={styles.editInput}
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => handleSave(row.key)}
                        onKeyDown={(e) => handleKeyDown(e, row.key)}
                        disabled={isSaving}
                        autoFocus
                      />
                    ) : (
                      <div className={styles.valueRow}>
                        <span>{row.totalNumberOfRequirement ?? '-'}</span>
                        <button
                          className={styles.editButton}
                          onClick={() => handleEditClick(row)}
                          aria-label="Edit"
                        >
                          <Icon name="edit" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default CaseLogSettings;
